/**
 * StatusBanner — appears at the top of all pages when any active incident exists.
 *
 * Features:
 *   - Shows most severe active incident (major_outage > partial_outage > degraded)
 *   - Dismissible via close button; persisted in localStorage per-incident
 *   - Dismissal shared across tabs via storage event listener
 *   - Auto-disappears within 30s when incident resolves (detected via polling)
 *   - Accessibility: role=alert, aria-live=assertive, keyboard-accessible close button
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { statusApi, type Incident, type ComponentStatus } from '../api/status.js';

// ── Severity ranking ──────────────────────────────────────────────────────────

const SEVERITY_RANK: Record<string, number> = {
  major_outage:   3,
  partial_outage: 2,
  degraded:       1,
};

function getIncidentSeverity(incident: Incident): number {
  return incident.affected_components.reduce((max, c) => {
    const rank = SEVERITY_RANK[c.status] ?? 0;
    return rank > max ? rank : max;
  }, 0);
}

function getMostSevereIncident(incidents: Incident[]): Incident | null {
  const active = incidents.filter(
    (i) => i.status !== 'resolved' &&
    i.affected_components.some((c) => SEVERITY_RANK[c.status] !== undefined),
  );

  if (active.length === 0) return null;

  return active.reduce((best, current) => {
    return getIncidentSeverity(current) > getIncidentSeverity(best) ? current : best;
  });
}

// ── Background colours ────────────────────────────────────────────────────────

const SEVERITY_BG: Record<string, string> = {
  major_outage:   'bg-red-50 border-red-200 text-red-800',
  partial_outage: 'bg-orange-50 border-orange-200 text-orange-800',
  degraded:       'bg-yellow-50 border-yellow-200 text-yellow-800',
};

const SEVERITY_CLOSE_HOVER: Record<string, string> = {
  major_outage:   'hover:text-red-900',
  partial_outage: 'hover:text-orange-900',
  degraded:       'hover:text-yellow-900',
};

function getSeverityKey(incident: Incident): string {
  const rank = getIncidentSeverity(incident);
  if (rank >= 3) return 'major_outage';
  if (rank === 2) return 'partial_outage';
  return 'degraded';
}

function dismissalKey(incidentId: string): string {
  return `status_banner_dismissed_${incidentId}`;
}

// ── Hook: poll for active incidents ───────────────────────────────────────────

const POLL_INTERVAL_MS = 30_000;
// Circuit-break: stop polling after this many consecutive failures so a
// dead status-service doesn't fill the network log with 502s every 30s.
const MAX_CONSECUTIVE_FAILURES = 3;

function useActiveBannerIncident() {
  const [incident, setIncident] = useState<Incident | null>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const mountedRef = useRef(true);
  const failuresRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const poll = useCallback(async () => {
    if (!mountedRef.current) return;
    let succeeded = false;
    try {
      const result = await statusApi.getIncidents({ limit: 50, offset: 0 });
      if (!mountedRef.current) return;
      const most = getMostSevereIncident(result.incidents);
      setIncident(most);
      succeeded = true;
      failuresRef.current = 0;
    } catch {
      failuresRef.current += 1;
    } finally {
      if (
        mountedRef.current &&
        failuresRef.current < MAX_CONSECUTIVE_FAILURES
      ) {
        // Exponential backoff while failing (1×, 4×, 16× cap), normal cadence
        // once we recover.
        const factor = succeeded ? 1 : Math.pow(4, failuresRef.current);
        const delay = Math.min(POLL_INTERVAL_MS * factor, 5 * 60_000);
        timerRef.current = setTimeout(() => {
          if (mountedRef.current) poll();
        }, delay);
      }
    }
  }, []);

  useEffect(() => {
    poll();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [poll]);

  return incident;
}

// ── Main Component ────────────────────────────────────────────────────────────

export function StatusBanner() {
  const incident = useActiveBannerIncident();
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  // On mount: load dismissed state from localStorage
  useEffect(() => {
    const dismissed = new Set<string>();
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key?.startsWith('status_banner_dismissed_') && localStorage.getItem(key) === 'true') {
        dismissed.add(key.replace('status_banner_dismissed_', ''));
      }
    }
    setDismissed(dismissed);
  }, []);

  // Listen for localStorage changes from other tabs
  useEffect(() => {
    function onStorage(event: StorageEvent) {
      if (!event.key?.startsWith('status_banner_dismissed_')) return;
      const incidentId = event.key.replace('status_banner_dismissed_', '');
      if (event.newValue === 'true') {
        setDismissed((prev) => new Set([...prev, incidentId]));
      } else if (event.newValue === null) {
        setDismissed((prev) => {
          const next = new Set(prev);
          next.delete(incidentId);
          return next;
        });
      }
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // When incident resolves, clear its dismissal from localStorage
  useEffect(() => {
    if (!incident) return;
    if (incident.status === 'resolved') {
      const key = dismissalKey(incident.id);
      localStorage.removeItem(key);
      setDismissed((prev) => {
        const next = new Set(prev);
        next.delete(incident.id);
        return next;
      });
    }
  }, [incident]);

  const handleDismiss = useCallback(() => {
    if (!incident) return;
    localStorage.setItem(dismissalKey(incident.id), 'true');
    setDismissed((prev) => new Set([...prev, incident.id]));
  }, [incident]);

  // Don't show if:
  // - No active incident
  // - Incident is resolved
  // - User dismissed this incident
  if (!incident) return null;
  if (incident.status === 'resolved') return null;
  if (dismissed.has(incident.id)) return null;

  const severityKey = getSeverityKey(incident);
  const colorClass = SEVERITY_BG[severityKey] ?? SEVERITY_BG.degraded;
  const closeHoverClass = SEVERITY_CLOSE_HOVER[severityKey] ?? '';

  return (
    <div
      role="alert"
      aria-live="assertive"
      className={`w-full border-b px-4 py-2.5 flex items-center gap-3 text-sm ${colorClass}`}
    >
      {/* Status dot */}
      <span className="flex-shrink-0 w-2 h-2 rounded-full bg-current opacity-70" aria-hidden="true" />

      {/* Message */}
      <div className="flex-1 min-w-0 flex flex-wrap items-center gap-2">
        <span className="font-medium truncate">{incident.title}</span>
        {incident.expected_resolution && (
          <span className="opacity-75 text-xs flex-shrink-0">
            Expected resolution: {new Date(incident.expected_resolution).toLocaleString()}
          </span>
        )}
        <Link
          to="/status"
          className="underline opacity-75 hover:opacity-100 text-xs flex-shrink-0"
        >
          View status page →
        </Link>
      </div>

      {/* Dismiss button */}
      <button
        type="button"
        onClick={handleDismiss}
        aria-label="Dismiss status banner"
        className={`
          flex-shrink-0 p-1 rounded
          opacity-70 ${closeHoverClass} hover:opacity-100
          focus:outline-none focus:ring-1 focus:ring-current
          transition-opacity
        `}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          className="w-4 h-4"
          aria-hidden="true"
        >
          <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
        </svg>
      </button>
    </div>
  );
}

export default StatusBanner;
